import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Select, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";
import { useState, useEffect, useCallback } from "react";

const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

function SubPlanList() {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  // State management
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} plans`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
    status: "all",
  });

  const getPlans = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching plans with params:", requestParams);
        const response = await masterService.getSubPlanList(requestParams);
        console.log("Countries API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const plansData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Plans data:", plansData);
          console.log("Pagination data:", paginationData);

          // Ensure countriesData is an array
          if (Array.isArray(plansData)) {
            setPlans(plansData);
          } else {
            console.error("Plans data is not an array:", plansData);
            setPlans([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch countries" });
          setPlans([]);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
        notify.error({ message: "Failed to fetch countries" });
        setPlans([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText, filters.status]
  );

  // Load countries data
  useEffect(() => {
    setPageTitle("Subscription Plans");
    getPlans();
  }, [setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getPlans({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getPlans({
      searchText: filters.searchText,
      status: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

      getPlans({
      searchText: filters.searchText,
      status: filters.status,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add country
  const handleAddPlan = () => {
    navigate("/plans/add/new");
  };

  // Handle edit country
  const handleEditPlan = (planId) => {
    navigate(`/plans/edit/${planId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "order_number",
      dataIndex: "order_number",
      key: "order_number",
      width: 120,
      render: (order_number) => <Tag color={"green"}>{order_number}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditPlan(record.id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Plans ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 300,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Select
            value={filters.status}
            onChange={handleStatusChange}
            placeholder="All"
            style={{ width: 140 }}
          >
            <Option value="all">All</Option>
            <Option value="Y">Active</Option>
            <Option value="N">Inactive</Option>
          </Select>
          <Search
            placeholder="Search plans..."
            allowClear
            onSearch={handleSearch}
            style={{ width: 320, maxWidth: "40vw" }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPlan}
            size="middle"
          >
            Add Plan
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={plans}
        rowKey="plan_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        size="middle"
        locale={{
          emptyText: "No plans found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
}

export default SubPlanList;
